import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Grid, Image, Link, Text } from '@chakra-ui/react'

import { CustomFormAnswerType } from '@/graphql/generated/schemas'

import {
  QuickReportDetailsT,
  QuickReportDisplayAnswerT,
} from '../../shared/types/feedTypes'
import { formatQuickReportDisplayAnswers } from '../../shared/utils/quickReportAnswers'

interface CustomQuickReportDetailIProps {
  details: QuickReportDetailsT
}

export const CustomQuickReportDetail = ({
  details,
}: CustomQuickReportDetailIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()

  if (!details?.displayAnswers?.length) {
    return null
  }

  // Group answers by section
  const answersBySection = formatQuickReportDisplayAnswers(
    details.displayAnswers
  ).reduce((acc, answer) => {
    const sectionName =
      answer.sectionName || t('incidents:quick_report_custom_general_section')
    if (!acc[sectionName]) {
      acc[sectionName] = []
    }
    acc[sectionName].push(answer)
    return acc
  }, {} as Record<string, QuickReportDisplayAnswerT[]>)

  return (
    <Box mb='20px' mt='20px'>
      {(
        Object.entries(answersBySection) as [
          string,
          QuickReportDisplayAnswerT[]
        ][]
      ).map(([sectionName, answers]) => (
        <Box key={sectionName} mb='16px'>
          <Text
            borderBottom='1px solid'
            borderBottomColor={`border.subtle.${mode}`}
            color={`text.secondary.${mode}`}
            fontSize='12px'
            fontWeight={600}
            mb='12px'
            pb='8px'
            textTransform='uppercase'
          >
            {sectionName}
          </Text>
          <Grid gap='12px 0px' templateColumns='175px 1fr'>
            {answers.map((answer) => (
              <AnswerRow answer={answer} key={answer.questionId} mode={mode} />
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  )
}

interface AnswerRowIProps {
  answer: QuickReportDisplayAnswerT
  mode: string
}

const AnswerRow = ({ answer, mode }: AnswerRowIProps) => {
  return (
    <>
      <Text color={`text.secondary.${mode}`} fontSize='14px' fontWeight={700}>
        {answer.label}
      </Text>
      <Box fontSize='14px' fontWeight={500}>
        <AnswerValue answer={answer} mode={mode} />
      </Box>
    </>
  )
}

const AnswerValue = ({ answer, mode }: AnswerRowIProps) => {
  // Handle FILE_UPLOAD type with file URLs
  if (
    answer.answerType === CustomFormAnswerType.FileUpload &&
    answer.fileUrls?.length
  ) {
    return (
      <Flex direction='column' gap='8px'>
        <Text color={`text.tertiary.${mode}`}>{answer.displayValue}</Text>
        <Flex flexWrap='wrap' gap='8px'>
          {answer.fileUrls.map((url, index) => (
            <FilePreview key={index} mode={mode} url={url} />
          ))}
        </Flex>
      </Flex>
    )
  }

  // Default text display
  return (
    <Text color={`text.primary.${mode}`}>{answer.displayValue || '-'}</Text>
  )
}

interface FilePreviewIProps {
  url: string
  mode: string
}

const FilePreview = ({ url, mode }: FilePreviewIProps) => {
  const { t } = useTranslation()
  const isImage = /\.(jpg|jpeg|png)/i.test(url) || url.includes('image')

  if (isImage) {
    return (
      <Link href={url} isExternal>
        <Image
          _hover={{ opacity: 0.8 }}
          alt={t('incidents:quick_report_custom_uploaded_file_alt')}
          borderRadius='4px'
          boxShadow='sm'
          height='80px'
          objectFit='cover'
          src={url}
          width='80px'
        />
      </Link>
    )
  }

  // Non-image file - show as link
  return (
    <Link
      _hover={{ textDecoration: 'underline' }}
      color={`text.link.${mode}`}
      href={url}
      isExternal
    >
      {t('incidents:quick_report_custom_view_file')}{' '}
      {url.split('/').pop()?.slice(0, 20)}
    </Link>
  )
}
